import {
  Box,
  Badge,
  Button,
  Checkbox,
  Flex,
  Image,
  Input,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom"; // Claude: LinkedIn callback detection 09-02-2026
import { MdArrowBack } from "react-icons/md"; // Claude: back button in dark header 27-03-2026
import Header from "../../Components/Common/Header";
import { useSelector, useDispatch } from "react-redux";
import actions from "../../redux/Actions";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { errorHandler } from "../../Utils/handler";
import Loader from "../../Components/Loader/Loader";
import { BuyerAPI } from "../../Apis/buyerAPI";
import { BASE_URL } from "../../Constants/apiConstant"; // Claude: LinkedIn OAuth redirect 09-02-2026
import { AiChatAPI } from "../../Apis/aiChatAPI"; // Claude: N19 — AI generate company info 04-03-2026
import { AdminAPI } from "../../Apis/adminAPI"; // Claude: industry list for profile 05-03-2026
import { Select as AntSelect } from "antd"; // Claude: industry multi-select 05-03-2026
import { Images } from "../../Assets/images"; // Claude: dark header logo 27-03-2026

type Props = {};

// Claude: Floating label field — defined outside component to prevent remount on each keystroke 17-02-2026
const FloatingField = ({ label, value, name, onChange, disabled, readOnly, type = "text", isTextarea, maxLength, rightLabel, alwaysGreen }: {
  label: string; value: string; name: string; onChange?: (e: any) => void;
  disabled?: boolean; readOnly?: boolean; type?: string; isTextarea?: boolean; maxLength?: number; rightLabel?: React.ReactNode; alwaysGreen?: boolean;
}) => {
  const [focused, setFocused] = useState(false);
  const isActive = focused || (value != null && value.length > 0);
  return (
    <Box position="relative" w="100%" pt="14px">
      {/* Claude: pointerEvents none on wrapper so label never blocks clicks on the input below 24-03-2026 */}
      <Box position="absolute" left={0} top={isActive ? "0" : "22px"} display="flex" alignItems="center" gap="4px" transition="all 0.2s ease" zIndex={1} pointerEvents="none">
        {/* Claude: green when filled/focused, gray when empty — no purple 24-03-2026 */}
        <Text fontSize={isActive ? "xs" : "sm"} color={alwaysGreen || isActive ? "green.600" : "gray.400"}>
          {label}
        </Text>
        {rightLabel && <Box pointerEvents="auto">{rightLabel}</Box>}
      </Box>
      {isTextarea ? (
        <Textarea
          variant="flushed"
          value={value}
          name={name}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          resize="none"
          color="text.black"
          borderColor="gray.400"
          _focus={{ borderColor: "green.600", boxShadow: "none" }}
          rows={2}
        />
      ) : (
        <Input
          variant="flushed"
          value={value}
          name={name}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={disabled}
          readOnly={readOnly}
          type={type}
          color="text.black"
          borderColor="gray.400"
          _focus={{ borderColor: "green.600", boxShadow: "none" }}
        />
      )}
    </Box>
  );
};

const Profile = (props: Props) => {
  const { user, token, verificationStatus, verificationDeadline } = useSelector((state: any) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate(); // Claude: back button 27-03-2026
  const [searchParams, setSearchParams] = useSearchParams(); // Claude: LinkedIn callback detection 09-02-2026
  const { error, success } = useToastWrapper();
  const infoToast = useToast(); // Claude: company type info toasts 12-04-2026
  const [loading, setLoading] = useState(false);
  const [linkedinVerified, setLinkedinVerified] = useState(false); // Claude: LinkedIn verification state 09-02-2026
  const [nameMismatch, setNameMismatch] = useState(false); // Claude: LinkedIn name mismatch state 17-02-2026
  const [linkedinName, setLinkedinName] = useState<string | null>(null); // Claude: name returned by LinkedIn on mismatch 31-03-2026
  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [data, setData] = useState<any>({
    name: "",
    companyName: "",
    designation: "",
    workEmail: "",
    socialEmail: "",
    phoneNumber: "",
    alternatePhoneNumber: "",
    companyWebsite: "",
    buyerPersonCity: "",
    buyerPersonState: "",
    buyerPersonCountry: "",
    industryIds: [],
    buyerTimezone: "",
    buyerCompanyWebsite: "", //Vishy added 03-June-2025
    buyerCompanyInfo: "" //Vishy added 03-June-2025
  });
  const [industryNodes, setIndustryNodes] = useState<any[]>([]); // Claude: industry list 05-03-2026
  const [companyType, setCompanyType] = useState<string[]>([]); // Claude: company type checkboxes 12-04-2026
  const [companyTypeOther, setCompanyTypeOther] = useState(""); // Claude: company type other text 12-04-2026

  const { isOpen: isLinkedinModalOpen, onOpen: onLinkedinModalOpen, onClose: onLinkedinModalClose } = useDisclosure(); // Claude: LinkedIn pre-verify modal 31-03-2026

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  const [flag, setFlag] = useState(false);

  // Claude: N19 — AI generate company info state 04-03-2026
  const [aiGenOpen, setAiGenOpen] = useState(false);
  const [aiGenText, setAiGenText] = useState("");
  const [aiGenLoading, setAiGenLoading] = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false); // Claude: once generated, disable button for session 12-Apr-2026
  const [aiKeywords, setAiKeywords] = useState("");
  const [aiGenError, setAiGenError] = useState("");

  const callAiGenerate = async (context: string) => {
    setAiGenLoading(true);
    setAiGenError("");
    try {
      const prompt = `Write a concise company description under 500 characters using this context: "${context}". Return only the description text, no preamble or labels.`;
      const res = await AiChatAPI.generateText(prompt);
      setAiGenText(res?.data?.text ?? "");
      setAiGenerated(true); // Claude: disable Generate with AI after successful generation 12-Apr-2026
    } catch (err: any) {
      setAiGenError(err?.message || "AI generation failed. Please try again.");
    } finally {
      setAiGenLoading(false);
    }
  };

  const handleOpenAiGen = () => {
    setAiGenOpen(true);
    setAiGenText("");
    setAiKeywords("");
    if (data?.buyerCompanyWebsite?.trim()) {
      callAiGenerate(data.buyerCompanyWebsite.trim());
    }
  };

  const getData = async () => {
    setLoading(true);
    try {
      const res = await BuyerAPI.getBuyerProfile();
      if (res) {
        console.log(res);
        const obj = {
          name: res.data.data?.buyerPersonName,
          companyName: res.data.data?.buyerCompanyName,
          designation: res.data.data?.buyerDesignation,
          workEmail: res.data.data?.buyerOfficialEmail,
          socialEmail: res.data.data?.buyersocialEmail,
          phoneNumber: res.data.data?.buyerPhoneNumber,
          buyerPersonCity: res.data.data?.buyerPersonCity || "",
          buyerPersonState: res.data.data?.buyerPersonState || "",
          buyerPersonCountry: res.data.data?.buyerPersonCountry || "",
          industryIds: res.data.data?.industryIds?.map((id: any) => typeof id === "object" ? String(id._id) : String(id)) || [],
          buyerTimezone: res.data.data?.buyerTimezone,
          buyerCompanyWebsite: res.data.data?.buyerCompanyWebsite, //Vishy added 03-June-2025
          buyerCompanyInfo: res.data.data?.buyerCompanyInfo //Vishy added 03-June-2025
        };
        setData(obj);
        setCompanyType(res.data.data?.buyerCompanyType || []); // Claude: company type 12-04-2026
        setCompanyTypeOther(res.data.data?.buyerCompanyTypeOther || ""); // Claude: company type other 12-04-2026
        setLinkedinVerified(res.data.data?.linkedinVerified || false); // Claude: LinkedIn verification state 09-02-2026
      }
    } catch (err) {
      console.log(err);
      error(errorHandler(err));
    } finally {
      setLoading(false);
    }
  };

  // Claude: Open LinkedIn OAuth in popup window, listen for result 17-02-2026
  const openLinkedInPopup = () => {
    onLinkedinModalClose();
    const url = `${BASE_URL}/auth/linkedin?token=${token}`;
    const popup = window.open(url, "linkedinVerify", "width=500,height=700,left=200,top=100");
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "linkedin-callback") {
        window.removeEventListener("message", handleMessage);
        if (event.data.status === "success") {
          success("LinkedIn profile verified successfully!");
          setLinkedinVerified(true);
          setNameMismatch(false);
        } else if (event.data.status === "name_mismatch") {
          setNameMismatch(true);
          setLinkedinVerified(false);
          if (event.data.linkedinName) setLinkedinName(event.data.linkedinName);
        } else {
          error("LinkedIn verification failed. Please try again.");
        }
        getData(); // refresh profile data
      }
    };
    window.addEventListener("message", handleMessage);
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const d = {
        buyerPersonName: data.name,
        buyerCompanyName: data.companyName,
        buyerDesignation: data.designation,
        buyersocialEmail: data.socialEmail,
        buyerPhoneNumber: data.phoneNumber,
        buyerPersonCity: data.buyerPersonCity,
        buyerPersonState: data.buyerPersonState,
        buyerPersonCountry: data.buyerPersonCountry,
        industryIds: data.industryIds,
        buyerTimezone: detectedTimezone,
        buyerCompanyWebsite: data.buyerCompanyWebsite, //Vishy added 03-June-2025
        buyerCompanyInfo: data.buyerCompanyInfo, //Vishy added 03-June-2025
        buyerCompanyType: companyType,
        buyerCompanyTypeOther: companyType.includes("Others") ? companyTypeOther : "", // Claude: company type 12-04-2026
      };
      const res = await BuyerAPI.updateBuyerProfile(d);
      if (res) {
        success("Profile Updated Successfully", "");
        // Claude: update verificationStatus in Redux after profile save 26-02-2026
        if (res.data?.data?.verificationStatus) {
          dispatch(actions.user.set({
            user,
            token,
            role: "Buyer",
            verificationStatus: res.data.data.verificationStatus,
            verificationDeadline: res.data.data.verificationDeadline ?? null,
            name: res.data.data.buyerPersonName ?? null, // Claude: keep name in sync after profile save 11-03-2026
          }));
        }
        setLoading(false);
        getData();
      }
    } catch (err) {
      console.log(err);
      error(errorHandler(err));
      setLoading(false);
    }
  };

  useEffect(() => {
    // Claude: If loaded in popup from LinkedIn callback, post result to parent and close 17-02-2026
    const linkedinStatus = searchParams.get("linkedin");
    const linkedinNameParam = searchParams.get("linkedin_name") ?? "";
    if (linkedinStatus && window.opener) {
      window.opener.postMessage({ type: "linkedin-callback", status: linkedinStatus, linkedinName: linkedinNameParam }, "*");
      window.close();
      return;
    }
    // Claude: Handle LinkedIn callback on same page (fallback if popup blocked) 17-02-2026
    if (linkedinStatus === "success") {
      success("LinkedIn profile verified successfully!");
      setLinkedinVerified(true);
      searchParams.delete("linkedin");
      searchParams.delete("linkedin_name");
      setSearchParams(searchParams, { replace: true });
    } else if (linkedinStatus === "name_mismatch") {
      setNameMismatch(true);
      if (linkedinNameParam) setLinkedinName(linkedinNameParam);
      searchParams.delete("linkedin");
      searchParams.delete("linkedin_name");
      setSearchParams(searchParams, { replace: true });
    } else if (linkedinStatus === "error") {
      error("LinkedIn verification failed. Please try again.");
      searchParams.delete("linkedin");
      setSearchParams(searchParams, { replace: true });
    }
    getData();
    // Claude: load industry list for multi-select 05-03-2026
    AdminAPI.getIndustryTree().then((res: any) => {
      const nodes = Array.isArray(res) ? res : (res?.data ?? []);
      setIndustryNodes(nodes.filter((n: any) => !n.parentId));
    }).catch(() => {});
  }, []);

  // Claude: compute days remaining + formatted deadline date for unverified notice 03-03-2026
  const daysRemaining = verificationDeadline
    ? Math.max(0, Math.ceil((new Date(verificationDeadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;
  const deadlineDate = verificationDeadline ? (() => {
    const d = new Date(verificationDeadline);
    return `${String(d.getDate()).padStart(2, '0')}-${d.toLocaleDateString('en-GB', { month: 'short' })}-${d.getFullYear()}`;
  })() : null;

  // Claude: warm gradient bg matching Buyers List page 24-03-2026
  return (
    <Box flexGrow={1} p={6} bg="#FFF5EE" minH="100dvh">
      {loading ? <Loader /> : null}
      {/* Claude: dark header with back button 27-03-2026 */}
      <Flex w={"100%"} mx={-6} mt={-6} px={4} py={5} mb={4} width={"calc(100% + 48px)"}
        bg={"#0E122F"} color={"white"} alignItems={"center"} justifyContent={"space-between"}>
        <Flex alignItems="center" gap={3} cursor="pointer" onClick={() => navigate(-1)}>
          <MdArrowBack size={20} color="white" />
          <Text fontSize={"xl"} fontWeight={700}>My Profile</Text>
        </Flex>
        <VStack spacing={0} alignItems="center" justifyContent="center">
          <Image src={Images.AftrLogo} h="32px" objectFit="contain" />
          <Text fontSize="11px" fontWeight={300} color="whiteAlpha.600" letterSpacing="wide">buyer (beta)</Text>
        </VStack>
      </Flex>
      {/* Claude: platform verification status — fine print, unverified/provisional/resubmitted only 03-03-2026 */}
      {(verificationStatus === "unverified" || verificationStatus === "provisional" || verificationStatus === "resubmitted") && (
        <Box mt={3} mb={1}>
          <Text fontSize="8px" color="#C62828" fontWeight={600}>
            {verificationStatus === "unverified" ? "Account status: Unverified"
              : verificationStatus === "resubmitted" ? "Account status: Submitted for verification — pending review"
              : "Account status: Provisional — under review"}
          </Text>
          {/* Claude: clickable support email 19-03-2026 */}
          <Text fontSize="8px" color="#C62828" mt="2px">
            {verificationStatus === "unverified" ? (
              <>Profile verification requires a valid Company Website that matches your Work Email domain. Contact{" "}
                <Text as="a" href="mailto:support@aftr.network" textDecoration="underline">support@aftr.network</Text>
                {" "}for details.{daysRemaining !== null ? ` Account will be restricted in ${daysRemaining} day${daysRemaining !== 1 ? "s" : ""}${deadlineDate ? ` (${deadlineDate})` : ""}.` : ""}</>
            ) : verificationStatus === "resubmitted" ? (
              <>Your profile has been submitted for verification. Our team will review it shortly. Contact{" "}
                <Text as="a" href="mailto:support@aftr.network" textDecoration="underline">support@aftr.network</Text>
                {" "}for details.</>
            ) : (
              <>Your profile is being reviewed by our team. Contact{" "}
                <Text as="a" href="mailto:support@aftr.network" textDecoration="underline">support@aftr.network</Text>
                {" "}for details.</>
            )}
          </Text>
        </Box>
      )}
      {/* Claude: Floating label underline form fields — replaces rounded rectangle inputs 17-02-2026 */}
      <Flex direction={"column"} w={"100%"} mt={"20px"} gap={"24px"}>
        {/* Claude: Full Name with LinkedIn Verified badge inline 17-02-2026 */}
        <Flex alignItems="flex-end" gap="8px">
          <Box flex={1}>
            <FloatingField label="Full Name" value={data.name} name="name" onChange={handleChange} />
          </Box>
          {/* Claude: LinkedIn badge — click to verify independently of profile save 26-02-2026 */}
          {linkedinVerified ? (
            <Badge
              colorScheme="green"
              fontSize="xs"
              px={2}
              py={0.5}
              mb="8px"
              borderRadius="6px"
              textTransform="none"
              whiteSpace="nowrap"
              cursor="pointer"
              onClick={onLinkedinModalOpen}
            >
              LinkedIn Verified
            </Badge>
          ) : nameMismatch ? (
            <Flex direction="column" gap={1}>
              <Badge
                bg="#F8D7DA"
                color="#721C24"
                fontSize="xs"
                px={2}
                py={0.5}
                borderRadius="6px"
                textTransform="none"
                whiteSpace="nowrap"
                cursor="pointer"
                onClick={onLinkedinModalOpen}
              >
                LinkedIn Name Mismatch
              </Badge>
              {/* Claude: show LinkedIn name and Apply button on mismatch 31-03-2026 */}
              {linkedinName && (
                <Flex alignItems="center" gap={1}>
                  <Text fontSize="10px" color="gray.500">Use: {linkedinName}</Text>
                  <Badge
                    fontSize="9px" px={1.5} py={0.5} borderRadius="4px"
                    bg="#E8F5E9" color="#2E7D32" cursor="pointer"
                    textTransform="none"
                    onClick={async () => {
                      try {
                        await (BuyerAPI as any).updateBuyerProfile({ buyerPersonName: linkedinName });
                        setData((prev: any) => ({ ...prev, name: linkedinName }));
                        openLinkedInPopup();
                      } catch { /* handled by API */ }
                    }}
                  >
                    Apply
                  </Badge>
                </Flex>
              )}
            </Flex>
          ) : (
            <Badge
              bg="#F8D7DA"
              color="#721C24"
              fontSize="xs"
              px={2}
              py={0.5}
              mb="8px"
              borderRadius="6px"
              textTransform="none"
              whiteSpace="nowrap"
              cursor="pointer"
              onClick={onLinkedinModalOpen}
            >
              LinkedIn
            </Badge>
          )}
        </Flex>

        {/* Company Name */}
        <FloatingField label="Company Name" value={data.companyName} name="companyName" onChange={handleChange} disabled />

        {/* Designation */}
        <FloatingField label="Designation" value={data.designation} name="designation" onChange={handleChange} alwaysGreen />

        {/* Work Email */}
        <FloatingField label="Work Email" value={data.workEmail} name="workEmail" onChange={handleChange} disabled />

        {/* Social Email */}
        <FloatingField label="Social Email" value={data.socialEmail} name="socialEmail" onChange={handleChange} />

        {/* Phone Number */}
        <FloatingField label="Phone Number" value={data?.phoneNumber} name="phoneNumber" onChange={handleChange} alwaysGreen />

        {/* Claude: City, State, Country, Industry added to buyer profile 05-03-2026 */}
        <FloatingField label="City" value={data?.buyerPersonCity || ""} name="buyerPersonCity" onChange={handleChange} alwaysGreen />
        <FloatingField label="State / Province" value={data?.buyerPersonState || ""} name="buyerPersonState" onChange={handleChange} />
        <FloatingField label="Country" value={data?.buyerPersonCountry || ""} name="buyerPersonCountry" onChange={handleChange} />
        <Box pt="14px" w="100%">
          <Text fontSize="xs" color="green.600" mb={1}>Industry</Text>
          <AntSelect
            mode="multiple"
            showSearch
            optionFilterProp="label"
            style={{ width: "100%" }}
            placeholder="Select industries"
            value={data?.industryIds || []}
            onChange={(values: string[]) => setData({ ...data, industryIds: values })}
            options={industryNodes.map((n: any) => ({ label: n.name, value: n._id }))}
            allowClear
          />
        </Box>

        {/* Claude: Company Type checkboxes 12-04-2026 */}
        <Box pt="14px" w="100%">
          <Text fontSize="xs" color="green.600" mb={2}>Company Type</Text>
          <Flex direction="column" gap={2}>
            {[
              { label: "Manufacturing", value: "Manufacturing" },
              { label: "Trade", value: "Trade" },
              { label: "Services / Solutions", value: "Services/Solutions" },
              { label: "Others", value: "Others" },
            ].map(({ label, value }) => (
              <Checkbox
                key={value}
                colorScheme="green"
                isChecked={companyType.includes(value)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setCompanyType([...companyType, value]);
                    if (value === "Trade") {
                      infoToast({ description: "Trade includes Distribution, Wholesale, Retail", status: "info", duration: 4000, isClosable: true, position: "top", containerStyle: { marginTop: "40vh", fontSize: "13px" } });
                    } else if (value === "Services/Solutions") {
                      infoToast({ description: "Includes all services: IT, financial, construction, and more", status: "info", duration: 4000, isClosable: true, position: "top", containerStyle: { marginTop: "40vh", fontSize: "13px" } });
                    }
                  } else {
                    setCompanyType(companyType.filter((t) => t !== value));
                  }
                }}
              >
                <Text fontSize="sm">{label}</Text>
              </Checkbox>
            ))}
            {companyType.includes("Others") && (
              <Input
                variant="flushed"
                placeholder="Please describe"
                value={companyTypeOther}
                onChange={(e) => setCompanyTypeOther(e.target.value)}
                fontSize="sm"
                borderColor="gray.400"
                _focus={{ borderColor: "green.600", boxShadow: "none" }}
                mt={1}
              />
            )}
          </Flex>
        </Box>

        {/* Timezone */}
        <FloatingField label="Timezone" value={detectedTimezone} name="buyerTimezone" readOnly />

        {/* Company Website */}
        <FloatingField label="Company Website" value={data.buyerCompanyWebsite} name="buyerCompanyWebsite" onChange={handleChange} />

        {/* Claude: N19 — Company Info with AI generate button 04-03-2026 */}
        <FloatingField
          label="Company Info (500 chars)"
          value={data.buyerCompanyInfo}
          name="buyerCompanyInfo"
          onChange={handleChange}
          isTextarea
          maxLength={500}
          rightLabel={
            <Button size="xs" variant="ghost" color="#7C66FF" fontSize="11px" h="18px" px={1} cursor="pointer" _hover={{ bg: "#7C66FF", color: "white" }} onClick={handleOpenAiGen} isDisabled={aiGenerated}>
              Generate with AI
            </Button>
          }
        />
      </Flex>

      {/* Claude: Save Profile button — LinkedIn is now independent 26-02-2026 */}
      <Flex w={"100%"} justifyContent={"center"} mt={"40px"}>
        <Button
          onClick={handleUpdate}
          w={"100%"}
          size={"sm"}
          background={"primary.main"}
          color={"white"}
          h={"40px"}
          borderRadius={"14px"}
          _hover={{ bg: "#2E7D32", color: "white" }}
        >
          Save Profile
        </Button>
      </Flex>

      <Button
        background={"transparent"}
        color={"button.red"}
        w={"100%"}
        h={"40px"}
        mt={"12px"}
      >
        Deactivate My Account
      </Button>

      {/* Claude: N19 — AI Generate Company Info modal 04-03-2026 */}
      <Modal isOpen={aiGenOpen} onClose={() => setAiGenOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="16px" mx={4}>
          <ModalCloseButton />
          <ModalBody py={5} px={5}>
            <Text fontSize="sm" fontWeight={600} mb={3} color="#0E122F">Generate Company Info with AI</Text>

            {aiGenError && <Text fontSize="12px" color="red.500" mb={3}>{aiGenError}</Text>}

            {/* Keywords input — shown when no website and no result yet */}
            {!data?.buyerCompanyWebsite?.trim() && !aiGenText && !aiGenLoading && (
              <Box mb={3}>
                <Text fontSize="12px" color="gray.500" mb={2}>
                  No website URL found. Enter a few keywords about your company:
                </Text>
                <Input
                  placeholder='e.g. "ERP software for manufacturing, India"'
                  fontSize="sm"
                  value={aiKeywords}
                  onChange={(e) => setAiKeywords(e.target.value)}
                  focusBorderColor="#7C66FF"
                  borderRadius="8px"
                  mb={3}
                />
                <Button
                  size="sm"
                  bg="#0E122F"
                  color="white"
                  borderRadius="8px"
                  _hover={{ bg: "#2E7D32" }}
                  isLoading={aiGenLoading}
                  isDisabled={!aiKeywords.trim()}
                  onClick={() => callAiGenerate(aiKeywords.trim())}
                >
                  Generate
                </Button>
              </Box>
            )}

            {aiGenLoading && <Text fontSize="sm" color="gray.500">Generating...</Text>}

            {aiGenText && !aiGenLoading && (
              <>
                <Textarea
                  value={aiGenText}
                  onChange={(e) => setAiGenText(e.target.value)}
                  fontSize="sm"
                  borderRadius="8px"
                  focusBorderColor="#7C66FF"
                  rows={5}
                  maxLength={500}
                  mb={1}
                />
                <Text fontSize="11px" color="gray.400" mb={3}>{aiGenText.length}/500</Text>
                <Flex gap={2} justifyContent="flex-end">
                  <Button size="sm" variant="ghost" onClick={() => setAiGenOpen(false)}>Cancel</Button>
                  <Button
                    size="sm"
                    bg="#0E122F"
                    color="white"
                    borderRadius="8px"
                    onClick={() => { setData({ ...data, buyerCompanyInfo: aiGenText }); setAiGenOpen(false); }}
                  >
                    Accept
                  </Button>
                </Flex>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Claude: LinkedIn pre-verify confirmation modal 31-03-2026 */}
      <Modal isOpen={isLinkedinModalOpen} onClose={onLinkedinModalClose} isCentered size="sm">
        <ModalOverlay />
        <ModalContent mx={4} borderRadius="16px">
          <ModalHeader fontSize="md" fontWeight={700}>LinkedIn Verification</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" lineHeight="1.6">
              Showcase your LinkedIn verified badge to the network.
            </Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button size="sm" variant="outline" onClick={onLinkedinModalClose}>Cancel</Button>
            <Button size="sm" bg="#0E122F" color="white" _hover={{ bg: "#1a2040" }} onClick={openLinkedInPopup}>Continue</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Profile;
